'use strict';

/**
 * Pure URL / parameter logic for the Search Filter Form.
 *
 * Every function here operates on plain inputs (strings, URLSearchParams, a selections
 * object) and returns values. Nothing in this module reads or writes the DOM, `window`,
 * `history`, schedules `requestAnimationFrame`, or fires callbacks — that keeps the logic
 * directly unit-testable and free of side effects. The impure glue lives in
 * `managers/url.js` (URLManager); DOM access lives in `utils/url-dom.js`.
 */

/**
 * Slugify a string for use in a URL path segment.
 *
 * @param {string} text - The text to slugify.
 * @return {string} The slug (empty string for falsy input).
 */
export function slugify(text) {
	if (!text) return '';
	return text
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, '') // Remove special chars except word chars, spaces, hyphens
		.replace(/\s+/g, '-') // Replace spaces with hyphens
		.replace(/-+/g, '-') // Replace multiple hyphens with single
		.replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Sanitize a parameter value for security while preserving legitimate URL-encoded structures.
 *
 * @param {string} value - The parameter value to sanitize.
 * @return {string} The sanitized value.
 */
export function sanitizeParameterValue(value) {
	if (!value || typeof value !== 'string') {
		return '';
	}

	// First, decode any URL encoding to work with the actual content
	let decodedValue;
	try {
		decodedValue = decodeURIComponent(value);
	} catch {
		// If decoding fails, use the original value
		decodedValue = value;
	}

	// Remove potentially dangerous characters while preserving legitimate content
	// Allow: alphanumeric, spaces, hyphens, underscores, brackets, plus signs, periods, commas
	const sanitized = decodedValue
		.replace(/[<>'"&]/g, '') // Remove HTML/XML characters
		.replace(/javascript:/gi, '') // Remove javascript: protocol
		.replace(/data:/gi, '') // Remove data: protocol
		.replace(/vbscript:/gi, '') // Remove vbscript: protocol
		.replace(/on\w+\s*=/gi, '') // Remove event handlers (onclick, onload, etc.)
		.trim();

	// Re-encode if the original was URL-encoded to preserve structure
	if (value !== decodedValue) {
		try {
			return encodeURIComponent(sanitized);
		} catch {
			return sanitized;
		}
	}

	return sanitized;
}

/**
 * Parse a form submission URL attribute value into path-only and optional query string.
 * Supports values like "/eap/amazon/find/" or "/eap/amazon/find/?utm_campaign=amazon-eap",
 * and tolerates a `?` stored/encoded as `%3F`. Empty input falls back to `['categories']`.
 *
 * @param {string} attributeValue - The raw attribute value.
 * @return {{ pathOnly: string, pathSegments: string[], queryString: string }} Raw path
 *   (preserving any trailing slash), path segments, and raw query string (including
 *   leading '?' or '').
 */
export function parseSubmissionUrl(attributeValue) {
	const defaultPath = '/categories';
	const defaultSegments = ['categories'];

	if (!attributeValue || attributeValue === '') {
		return {
			pathOnly: defaultPath,
			pathSegments: defaultSegments,
			queryString: '',
		};
	}

	// Normalize so we detect query string even when ? is stored/encoded as %3F
	const normalized = attributeValue.replace(/%3F/gi, '?');

	const queryIndex = normalized.indexOf('?');
	const pathOnly =
		queryIndex === -1 ? normalized : normalized.slice(0, queryIndex);
	const queryString = queryIndex === -1 ? '' : normalized.slice(queryIndex);

	const pathSegments = pathOnly.split('/').filter((segment) => segment !== '');

	return { pathOnly, pathSegments, queryString };
}

/**
 * Build the set of known form parameter names from the parameter config.
 *
 * @param {Object} parameterConfig - The parameter configuration map.
 * @return {Set<string>} Set of form parameter names.
 */
export function getFormParamNames(parameterConfig) {
	const names = new Set();
	Object.values(parameterConfig).forEach((config) => {
		names.add(config.paramName);
	});
	return names;
}

/**
 * Determine whether a parameter key is a specialty parameter key.
 *
 * @param {string} key    - The parameter key to check.
 * @param {Object} config - The specialty parameter config.
 * @return {boolean} True if the key is a specialty key.
 */
export function isSpecialtyKey(key, config) {
	return (
		key.startsWith(config.paramName + '[') ||
		key.startsWith(
			config.paramName.charAt(0).toUpperCase() +
				config.paramName.slice(1) +
				'[',
		) ||
		key.startsWith(config.paramName.toUpperCase() + '[') ||
		key.toLowerCase().includes(config.paramName) ||
		key.toLowerCase().includes('need')
	);
}

/**
 * Determine whether a parameter key is a known form parameter.
 *
 * @param {string}      key             - The parameter key to check.
 * @param {Set<string>} formParamNames  - Precomputed set of form parameter names.
 * @param {Object}      specialtyConfig - The specialty parameter config (for key matching).
 * @return {boolean} True if it's a form parameter.
 */
export function isFormParameter(key, formParamNames, specialtyConfig) {
	if (formParamNames.has(key)) {
		return true;
	}

	if (specialtyConfig && isSpecialtyKey(key, specialtyConfig)) {
		return true;
	}

	return false;
}

/**
 * Determine whether the given params contain any specialty parameters.
 *
 * @param {URLSearchParams} urlParams - The URL parameters.
 * @param {Object}          config    - The specialty parameter config.
 * @return {boolean} True if specialty params are present.
 */
export function hasSpecialtyParams(urlParams, config) {
	return (
		urlParams.has(config.paramName) ||
		urlParams.has(
			config.paramName.charAt(0).toUpperCase() + config.paramName.slice(1),
		) ||
		urlParams.has(config.paramName.toUpperCase()) ||
		Array.from(urlParams.keys()).some(
			(key) =>
				key.startsWith(config.paramName + '[') ||
				key.toLowerCase().includes(config.paramName) ||
				key.toLowerCase().includes('need'),
		)
	);
}

/**
 * Determine whether any parameters relevant to the form are present.
 *
 * @param {URLSearchParams} urlParams       - The URL parameters.
 * @param {Object}          parameterConfig - The parameter configuration map.
 * @return {boolean} True if any relevant form parameter is present.
 */
export function hasRelevantParams(urlParams, parameterConfig) {
	return Object.values(parameterConfig).some((config) => {
		if (config.singleSelect) {
			return urlParams.has(config.paramName);
		}
		return hasSpecialtyParams(urlParams, config);
	});
}

/**
 * Parse generic (non-form) URL parameters, sanitizing each value.
 *
 * @param {URLSearchParams} urlParams       - The URL parameters to parse.
 * @param {Set<string>}     formParamNames  - Precomputed set of form parameter names.
 * @param {Object}          specialtyConfig - The specialty parameter config.
 * @return {Map<string, string>} Map of sanitized generic parameters.
 */
export function parseGenericParams(urlParams, formParamNames, specialtyConfig) {
	const genericParams = new Map();

	for (const [key, value] of urlParams.entries()) {
		// Skip empty keys or values
		if (!key || !value) {
			continue;
		}

		// Skip form parameters; only keep generic ones
		if (!isFormParameter(key, formParamNames, specialtyConfig)) {
			const sanitizedValue = sanitizeParameterValue(value);

			// Only store if sanitized value is not empty
			if (sanitizedValue) {
				genericParams.set(key, sanitizedValue);
			}
		}
	}

	return genericParams;
}

/**
 * Parse specialty parameter values from the URL parameters, falling back to regex
 * matching over a raw search string when they can't be read directly.
 *
 * @param {URLSearchParams} urlParams    - The URL parameters.
 * @param {Object}          config       - The specialty parameter config (incl. patterns).
 * @param {string}          searchString - Raw query string used for the regex fallback.
 * @return {string[]} The specialty values.
 */
export function parseSpecialtyParams(urlParams, config, searchString) {
	const values = urlParams.getAll(config.paramName);
	if (values.length > 0) return values;

	const valueSet = new Set();

	for (const [key, value] of urlParams.entries()) {
		if (isSpecialtyKey(key, config)) {
			valueSet.add(decodeURIComponent(value));
		}
	}

	if (valueSet.size === 0) {
		for (const pattern of config.patterns) {
			const matches = searchString.matchAll(pattern);
			for (const match of matches) {
				valueSet.add(decodeURIComponent(match[1]));
			}
		}
	}

	return Array.from(valueSet);
}

/**
 * Build ordered search parameters (the redirect URL query) from form selections.
 * Order: setting → address → typeOfCare → state → insurance → specialty[i].
 * Form parameters only — generic parameters are appended separately by the caller.
 *
 * @param {Object} selections - Map of input name → array of selected values.
 * @return {URLSearchParams} The ordered search parameters.
 */
export function orderSearchParams(selections) {
	const searchParams = new URLSearchParams();

	// 1. setting (always first)
	searchParams.append('setting', 'Virtual');

	// 2. address (if state is selected)
	if (selections['states-options']) {
		const stateValue = selections['states-options'][0];
		searchParams.append('address', `${stateValue}, USA`);
	}

	// 3. typeOfCare
	if (selections['type-of-care-options']) {
		searchParams.append('typeOfCare', selections['type-of-care-options'][0]);
	}

	// 4. state
	if (selections['states-options']) {
		searchParams.append('state', selections['states-options'][0]);
	}

	// 5. insurance
	if (selections['payors-options']) {
		searchParams.append('insurance', selections['payors-options'][0]);
	}

	// 6. specialties (if any)
	if (selections['specialties-options']) {
		selections['specialties-options'].forEach((value, index) => {
			searchParams.append(`specialty[${index}]`, value);
		});
	}

	return searchParams;
}

/**
 * Build the filter parameters pushed to browser history.
 *
 * @param {Object} selections - Map of input name → array of selected values.
 * @return {URLSearchParams} The filter parameters.
 */
export function buildFilterParams(selections) {
	const filterParams = new URLSearchParams();

	for (const [name, values] of Object.entries(selections)) {
		if (name === 'specialties-options') {
			values.forEach((value, index) => {
				filterParams.append(`specialty[${index}]`, value);
			});
		} else if (name === 'states-options') {
			filterParams.append('state', values[0]);
		} else if (name === 'payors-options') {
			filterParams.append('insurance', values[0]);
		} else if (name === 'type-of-care-options') {
			filterParams.append('typeOfCare', values[0]);
		}
	}

	return filterParams;
}

/**
 * Assemble the final search URL from already-resolved inputs.
 *
 * State/insurance path slugs are pushed onto the path (unless omitted), and the custom
 * query string (e.g. UTM from form_submission_url) is merged with the form params, with
 * form params taking precedence for overlapping keys.
 *
 * @param {Object}          params                        - Assembly inputs.
 * @param {boolean}         params.hasTrailingSlash       - Whether the base path has a trailing slash.
 * @param {string[]}        params.pathSegments           - Base path segments.
 * @param {string}          params.queryString            - Custom query string (with leading '?' or '').
 * @param {URLSearchParams} params.searchParams           - The form search parameters.
 * @param {?string}         params.stateSlug              - State path slug, or null to omit.
 * @param {?string}         params.insuranceSlug          - Insurance path slug, or null to omit.
 * @param {boolean}         params.omitStateInsurancePath - Whether to omit state/insurance path segments.
 * @return {string} The final search URL.
 */
export function assembleSearchUrl({
	hasTrailingSlash,
	pathSegments,
	queryString,
	searchParams,
	stateSlug,
	insuranceSlug,
	omitStateInsurancePath,
}) {
	const segments = [...pathSegments];

	if (!omitStateInsurancePath) {
		// A non-null slug means the corresponding value was selected and should be added,
		// even when the slug itself is an empty string (matches prior behavior).
		if (stateSlug !== null) {
			segments.push(stateSlug);
		}
		if (insuranceSlug !== null) {
			segments.push(insuranceSlug);
		}
	}

	// Drop empty segments so an empty slug (e.g. slugify of a symbol-only value) never
	// introduces a doubled slash — whether it lands mid-path ('/quiz//aetna') or, with
	// hasTrailingSlash, at the end ('/quiz/california//').
	let baseUrl = '/' + segments.filter((segment) => segment !== '').join('/');

	if (hasTrailingSlash && !baseUrl.endsWith('/')) {
		baseUrl += '/';
	}

	// Merge custom URL query params (e.g. UTM from form_submission_url) with form params.
	// Form params take precedence for overlapping keys.
	const mergedParams = new URLSearchParams();
	if (queryString && queryString.startsWith('?')) {
		for (const [key, value] of new URLSearchParams(
			queryString.slice(1),
		).entries()) {
			mergedParams.set(key, value);
		}
	}
	for (const [key, value] of searchParams.entries()) {
		mergedParams.set(key, value);
	}

	return baseUrl + '?' + mergedParams.toString();
}
